import { Container, Row, Col, Button } from "react-bootstrap";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import DateRangePicker from "../../../../common/CustomDateRangePicker";
import { useEffect, useRef, useState } from "react";
import SearchableMultiSelectDropdown from "../../../../common/SearchableMultiSelectDropdown";
import { PICKLIST_GROUP_NAME } from "../../../../common/Constant";
import {
  fetchCaseheaderinfo,
  fetchPickListByGroupName,
} from "../../../../services/CaseInformationApi";
import SearchInput from "../../../../common/InlineSearch";
import {
  getCaseActivityData,
  getCaseActivitySummaryData,
  getCaseActivitySummaryList,
  getCaseActivityUsers,
} from "../../../../services/CaseActivityAPI";

import { RootState } from "../../../../redux/Store";
import { useSelector } from "react-redux";
import Loader from "../../../../common/Loader";
import DailySummaryAccordion from "./DailySummaryAccordian";
import { formatAppointmentDateTime } from "../../../../utils/dateUtils";
import { ExportActivityHeader } from "../actions/constant";

interface ActivityListProps {
  userData: any;
  action: string;
  loggedInUserid: any;
  caseStageId: any;
  onCancel: (action: string) => void;
  shouldRefreshActivity: boolean;
  setShouldRefreshActivity: React.Dispatch<React.SetStateAction<boolean>>;
}

const CaseActivityList: React.FC<ActivityListProps> = ({
  userData,
  action,
  loggedInUserid,
  caseStageId,
  onCancel,
  shouldRefreshActivity,
  setShouldRefreshActivity,
}) => {
  const caseInfoID = useSelector(
    (state: RootState) => state.caseHeader.caseInfoId,
  );

  const getCaseInfoID = userData?.caseInfoId ? userData.caseInfoId : caseInfoID;

  const today = new Date();
  const [closureDate, setClosureDate] = useState<Date | null>(null);
  const searchInputRef = useRef<any>(null);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [activityData, setActivityData] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, today]);
  const [createdDate, setCreatedDate] = useState<any>("");

  const [activityTypeOption, setActivityTypeOption] = useState<
    { value: string; label: string }[]
  >([]);
  const [activityType, setActivityType] = useState<(string | number)[]>([]);

  const [caseStageOption, setCaseStageOption] = useState<
    { value: string; label: string }[]
  >([]);
  const [stage, setStage] = useState<(string | number)[]>([]);

  const [activityOption, setActivityOption] = useState<
    { value: string; label: string }[]
  >([]);
  const [activity, setActivity] = useState<(string | number)[]>([]);

  const [addedByOption, setAddedByOption] = useState<
    { value: string; label: string }[]
  >([]);
  const [addedBy, setAddedBy] = useState<(string | number)[]>([]);

  const handleDateRangeChange = (newRange: [Date | null, Date | null]) => {
    const [start, end] = newRange;
    setDateRange([start ?? undefined, end ?? undefined]);
  };

  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<any>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchQuerylength, setSearchQuerylength] = useState<string>("");
  const hasFetchedInitially = useRef(false);
  const [resetTriggered, setResetTriggered] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [shouldFetchSummary, setShouldFetchSummary] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [yearSummary, setYearSummary] = useState<any[]>([]);
  const [monthSummary, setMonthSummary] = useState<any[]>([]);
  const [dailySummary, setDailySummary] = useState<any[]>([]);

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const [pageNumber, setPageNumber] = useState(1);
  const [allLoaded, setAllLoaded] = useState(false); // when all data fetched
  const [isScrolled, setIsScrolled] = useState(false);


  const fetchCaseInfo = async () => {
    try {
      const headerInfo = await fetchCaseheaderinfo(getCaseInfoID);

      if (headerInfo) {
        setCreatedDate(headerInfo?.createdDate);
        const createdOnDate = new Date(headerInfo?.createdDate);
        const closure = headerInfo?.closureDate
          ? new Date(headerInfo.closureDate)
          : null;

        setClosureDate(closure);
        setDateRange([createdOnDate, closure || new Date()]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (getCaseInfoID) {
      fetchCaseInfo();
    }
  }, [getCaseInfoID]);

  const params = {
    activity: {
      groupName: PICKLIST_GROUP_NAME.Appointment_Status,
      triggerById: loggedInUserid,
    },
    stage: {
      groupName: PICKLIST_GROUP_NAME.Stages_of_Case,
      triggerById: loggedInUserid,
    },
  };


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);




  const ACTIVITY_TYPE = [
    {
      value: "Activity",
      label: "Activity",
    },
    {
      value: "Notes",
      label: "Notes",
    },
  ];

  const fetchActivityDropdownData = async () => {
    try {
      setActivityTypeOption(ACTIVITY_TYPE || []);

      const stagePicklistData = await fetchPickListByGroupName(params.stage);
      setCaseStageOption(stagePicklistData || []);

      const activitySummaryData = await getCaseActivitySummaryList();
      setActivityOption(activitySummaryData || []);

      const addedByData = await getCaseActivityUsers(getCaseInfoID);
      setAddedByOption(addedByData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchActivitySummary = async (query: string = "") => {
    const monthString =
      selectedMonth.length === 1 && selectedMonth[0] === 0
        ? "ALL"
        : selectedMonth.length > 0
          ? selectedMonth.join(",")
          : null;

    const activitySummaryRequest = {
      caseInfoId: getCaseInfoID,
      startDate: null,
      endDate: null,
      type: activityType?.length ? activityType.join(",") : null,
      stageIds: stage?.length ? stage.join(",") : null,
      activityIds: activity?.length ? activity.join(",") : null,
      addedByIds: addedBy?.length ? addedBy.join(",") : null,
      searchString: query || null,
      activityYear: selectedYear,
      activityMonth: monthString,
    };

    try {
      const response = await getCaseActivitySummaryData(activitySummaryRequest);

      const result = response?.result || [];

      if (result) {
        setYearSummary(result?.yearSummary || []);
        setMonthSummary(result?.monthSummary || []);
        setDailySummary(result?.dailySummary || []);
      }
    } catch (error) {
      console.error("Error fetching activity data:", error);
    }
  };

  useEffect(() => {
    if (getCaseInfoID && (selectedYear || isInitialLoad)) {
      fetchActivitySummary(searchQuery);
      setIsInitialLoad(false);
    }
  }, [getCaseInfoID, selectedYear, selectedMonth]);

  useEffect(() => {
    if (shouldFetchSummary && selectedYear != null) {
      fetchActivitySummary(searchQuery);
      setShouldFetchSummary(false);
    }
  }, [shouldFetchSummary, selectedYear, selectedMonth]);

  useEffect(() => {
    if (yearSummary.length > 0 && !selectedYear) {
      setSelectedYear(yearSummary[0].activityYear);
    }
    if (monthSummary.length > 0 && selectedMonth.length === 0) {
      setSelectedMonth([0]);
    }
  }, [yearSummary, monthSummary]);

  const handleMonthClick = (monthNo: number) => {
    setSelectedMonth((prev) => {
      if (monthNo === 0) {
        return [0]; // only ALL
      }
      let updated = prev.filter((m) => m !== 0);

      if (updated.includes(monthNo)) {
        updated = updated.filter((m) => m !== monthNo);
      } else {
        updated = [...updated, monthNo];
      }

      return updated;
    });
  };

  const fetchActivityList = async (
    query: string = "",
    pageNumber: number = 1,
    pageSize: number = itemsPerPage,
    key: any = "",
    direction: any = "DESC",
  ) => {
    const [start, end] = dateRange;
    const monthString =
      selectedMonth.length === 1 && selectedMonth[0] === 0
        ? "ALL"
        : selectedMonth.length > 0
          ? selectedMonth.join(",")
          : null;

    const activityRequest = {
      caseInfoId: getCaseInfoID,
      // startDate: start ? start.toISOString().split("T")[0] : null,
      // endDate: end ? end.toISOString().split("T")[0] : null,
      startDate: null,
      endDate: null,
      type: activityType?.length ? activityType.join(",") : null,
      stageIds: stage?.length ? stage.join(",") : null,
      activityIds: activity?.length ? activity.join(",") : null,
      addedByIds: addedBy?.length ? addedBy.join(",") : null,
      searchString: query || null,
      activityYear: selectedYear,
      activityMonth: monthString,
      transactionDate: selectedDate,
      pageNumber,
      pageSize,
      sortColumn: key,
      sortOrder: direction,
      triggerById: loggedInUserid,
    };

    try {
      setLoading(true);
      const activityDataList = await getCaseActivityData(activityRequest);

      if (activityDataList?.result) {
        const formattedData = activityDataList.result.map((item: any) => {
          // Format date as MM/DD/YYYY
          const dateObj = new Date(item.transactionDate);
          const month = String(dateObj.getMonth() + 1).padStart(2, "0"); // 01-12
          const day = String(dateObj.getDate()).padStart(2, "0"); // 01-31
          const year = dateObj.getFullYear();
          const formattedDate = `${month}/${day}/${year}`;

          // Format time as HH:mm
          const timeObj = new Date(`1970-01-01T${item.transactionTime}`);
          const formattedTime = timeObj
            .toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
            .replace(/am/g, "AM")
            .replace(/pm/g, "PM");

          const cleanedDescription = item.activityDescription.replace(
            /\(<b>(.*?)<\/b>\)/g,
            "<b>$1</b>",
          );

          return {
            ...item,
            transactionDate: formattedDate,
            transactionTime: formattedTime,
            activityDescription: cleanedDescription,
          };
        });

        setActivityData((prev: any[]) => {
          const updatedData =
            pageNumber === 1 ? formattedData : [...prev, ...formattedData];

          const totalCount = formattedData[0]?.totalCount || 0;

          if (updatedData.length >= totalCount) {
            setAllLoaded(true);
          } else {
            setAllLoaded(false);
          }

          return updatedData;
        });
        setFilteredData(formattedData);

        const totalCount = formattedData[0]?.totalCount || 0;

        if (
          (pageNumber === 1 ? 0 : activityData.length) + formattedData.length >=
          totalCount
        ) {
          setAllLoaded(true);
        } else {
          setAllLoaded(false);
        }
        setTotalRows(formattedData[0]?.totalCount || null);
      }
    } catch (error) {
      console.error("Error fetching activity data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shouldRefreshActivity) {
      fetchActivityList();
      setShouldRefreshActivity(false); // reset the flag
    }
  }, [shouldRefreshActivity]);

  useEffect(() => {
    if (selectedYear != null && selectedDate) {
      fetchActivityList("", pageNumber);
    }
  }, [selectedDate, pageNumber]);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      fetchActivityDropdownData();
      // Run API once on initial mount only
      if (!hasFetchedInitially.current) {
        fetchActivityList();

        hasFetchedInitially.current = true;
      }
    }
  }, [dateRange]);

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    setPageNumber(1); // reset pagination
    setShouldFetch(true);
    setShouldFetchSummary(true);
  };

  useEffect(() => {
    if (shouldFetch && selectedYear != null && selectedDate) {
      fetchActivityList(searchQuery, pageNumber);
      setShouldFetch(false);
    }
  }, [shouldFetch, selectedDate, pageNumber, selectedYear, selectedMonth]);

  const handleSearchQueryChangeForlength = (query: string) => {
    setSearchQuerylength(query?.trim());
  };

  const handleReset = () => {
    searchInputRef.current?.clear();
    setSearchQuery("");
    setSearchQuerylength("");
    setPageNumber(1);
  };

  const handleResetFilters = () => {
    const createdOnDate = createdDate ? new Date(createdDate) : undefined;
    const today = new Date();

    setActivityType([]);
    setStage([]);
    setActivity([]);
    setAddedBy([]);
    setDateRange([createdOnDate, today]);

    setShouldFetch(true);
    setShouldFetchSummary(true);
  };

  const handleExportExcel = async () => {
    const sheetName = "Activity Summary Report";
    const title = "Activity Summary Report";

    const headers = [
      "Sr. No",
      ...ExportActivityHeader.map((h: any) => h.label),
    ];

    const [start, end] = dateRange;
    const monthString =
      selectedMonth.length === 1 && selectedMonth[0] === 0
        ? "ALL"
        : selectedMonth.length > 0
          ? selectedMonth.join(",")
          : null;

    const activityRequest = {
      caseInfoId: getCaseInfoID,
      startDate: start ? start.toISOString().split("T")[0] : null,
      endDate: end ? end.toISOString().split("T")[0] : null,
      type: activityType?.length ? activityType.join(",") : null,
      stageIds: stage?.length ? stage.join(",") : null,
      activityIds: activity?.length ? activity.join(",") : null,
      addedByIds: addedBy?.length ? addedBy.join(",") : null,
      searchString: searchQuery || null,
      activityYear: selectedYear,
      activityMonth: monthString,
      transactionDate: null,
      pageNumber: null,
      pageSize: null,
      sortColumn: "",
      sortOrder: "DESC",
      triggerById: loggedInUserid,
    };

    try {
      const activityDataList = await getCaseActivityData(activityRequest);

      if (activityDataList?.result?.length) {
        const exportData = activityDataList.result.map(
          (item: any, index: number) => {
            const cleanedDescription = item.activityDescription
              ?.replace(/<b>\s*<\/b>/g, "")
              .replace(/<\/?b>/g, "");

            return [
              index + 1,
              ...ExportActivityHeader.map((h: any) => {
                if (h.key === "transactionDate") {
                  return formatAppointmentDateTime(item?.txn_Dt) || "";
                }
                if (h.key === "activityDescription") {
                  return cleanedDescription || "";
                }
                return item[h.key] ?? "";
              }),
            ];
          },
        );

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(sheetName);

        // Title
        worksheet.mergeCells(1, 1, 1, headers.length);
        const titleCell = worksheet.getCell(1, 1);
        titleCell.value = title;
        titleCell.font = { bold: true, size: 14 };
        titleCell.alignment = { horizontal: "center", vertical: "middle" };

        // Header
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };

        // Data
        exportData.forEach((row: any[]) => {
          worksheet.addRow(row);
        });

        // Column width
        worksheet.columns.forEach((col, index) => {
          if (col) {
            col.width = index === 0 ? 10 : 25;
          }
        });

        // Center align "Sr. No" column
        worksheet.getColumn(1).eachCell((cell) => {
          cell.alignment = { horizontal: "center", vertical: "middle" };
        });

        const buffer = await workbook.xlsx.writeBuffer();

        saveAs(
          new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          `${title}.xlsx`,
        );
      }
    } catch (error) {
      console.error("Error fetching activity data:", error);
    }
  };

  return (
    <Container fluid className="pb-5">
      <Loader isLoading={loading} />
      <Row>
        <Col>
          <h5 className="mb-3">Activity List</h5>
        </Col>
      </Row>

      <Row>
        <Col sm={3}>
          <div className="date-picker-container customDrodownControlDAtepicker">
            <DateRangePicker
              label="Date"
              onChange={handleDateRangeChange}
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              minDate={
                userData?.createdOn ? new Date(userData.createdOn) : undefined
              }
              maxDate={closureDate || new Date()}
            />
          </div>
        </Col>

        <Col sm={2} className="selection_dropdown adj_label_lt">
          <SearchableMultiSelectDropdown
            label="Type"
            name="Type"
            options={activityTypeOption}
            values={activityType}
            onChange={(e, name, values) => setActivityType(values)}
          />
        </Col>
        <Col sm={2} className="selection_dropdown adj_label_lt">
          <SearchableMultiSelectDropdown
            label="Stage"
            name="Stage"
            options={caseStageOption}
            values={stage}
            onChange={(e, name, values) => setStage(values)}
          />
        </Col>
        <Col sm={2} className="selection_dropdown adj_label_lt">
          <SearchableMultiSelectDropdown
            label="Activity"
            name="Activity"
            options={activityOption}
            values={activity}
            onChange={(e, name, values) => setActivity(values)}
          />
        </Col>
        <Col sm={2} className="selection_dropdown adj_label_lt">
          <SearchableMultiSelectDropdown
            label="Added By"
            name="AddedBy"
            options={addedByOption}
            values={addedBy}
            onChange={(e, name, values) => setAddedBy(values)}
          />
        </Col>
        <Col sm={1} className="d-flex align-items-center px-0">
          <Button
            className="reset_btn"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "10px",
              alignItems: "center",
              display: "flex",
              fontSize: "22px",
            }}
            onClick={() => {
              setPageNumber(1);
              setShouldFetch(true);
              setShouldFetchSummary(true);
            }}
          >
            <span className="material-icons reset_ic0">search</span>
          </Button>

          <Button
            variant="outline"
            className="btn d-flex align-items-center btn-text-border"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "10px",
              marginLeft: "10px", // space between buttons
              alignItems: "center",
              fontSize: "22px",
              display: "flex",
            }}
            onClick={handleResetFilters}
          >
            <span className="material-icons reset_ic0">restart_alt</span>
          </Button>
        </Col>
      </Row>

      {/* Horizontal line added here */}
      <hr style={{ marginTop: "10px", height: "1px", marginBottom: "8px" }} />
      <div className={`activity_wrapperaTop ${isScrolled ? "scrolled" : ""}`}>
        <Row>
          <div className="d-flex  search_closer align-items-center">
            {/* <div className="sectionTitle noborder me-auto">List</div> */}

            <div className="col-md-12">
              <div className="row p-0 m-0">
                <div className="col-md-9 p-0 m-0 monthPill">
                  {yearSummary.map((item: any) => {
                    const isActive = selectedYear === item.activityYear;

                    return (
                      <Button
                        key={item.activityYear}
                        size="sm"
                        style={{ marginRight: "5px" }}
                        variant={isActive ? "primary" : "outline-primary"}
                        className={isActive ? "year-btn active" : "year-btn"}
                        onClick={() => {
                          setSelectedYear(item.activityYear);

                          setSelectedMonth([0]);
                        }}
                      >
                        {item.activityYear}{" "}
                        <span className="counter_activity">
                          {item.activityCount >= 100 ? "99+" : item.activityCount}
                        </span>
                      </Button>
                    );
                  })}

                </div>
                <div className="col-md-3">
                  <div className="d-flex">
                    <SearchInput
                      ref={searchInputRef}
                      data={activityData}
                      searchKeys={[]}
                      onSearchResult={setFilteredData}
                      placeholder="Search"
                      onSearchQueryChange={handleSearchQueryChange}
                      onInputChange={handleSearchQueryChangeForlength}
                      activitySearchWidth={true}
                    />
                    <button
                      onClick={handleReset}
                      className="reset_btn p-2 btn"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "5px",
                      }}
                    >
                      <span className="material-icons reset_ic0">restart_alt</span>
                    </button>
                    <Button
                      variant="outline"
                      className="d-flex align-items-center btn-text-border p-2 adjustBtuu"
                      onClick={handleExportExcel}
                    // disabled={!appointmentData?.length}
                    >
                      <span className="material-symbols-outlined">download</span>
                    </Button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </Row>
      </div>
      <div className={`activity_wrappera ${isScrolled ? "scrolled" : ""}`}>
        <Row className="mt-0 mb-1 fade-in-left">
          <Col className="d-flex gap-2 flex-wrap ">

          </Col>
        </Row>
        <div style={{ background: "#0092D5", height: "2px" }}></div>
        <Row className="mt-2 mb-2 fade-in-left">
          <Col className="d-flex gap-2 flex-wrap">
            {monthSummary.map((item: any) => {
              const isActive = selectedMonth.includes(item.activityMonthNo);

              return (
                <Button
                  key={item.activityMonthNo}
                  size="sm"
                  variant={isActive ? "primary" : "outline-primary"}
                  className={isActive ? "month-btn active" : "month-btn"}
                  onClick={() => handleMonthClick(item.activityMonthNo)}
                >
                  {item.activityMonth}{" "}
                  <span className="month_badgeActivity">
                    {item.activityCount >= 100 ? "99+" : item.activityCount}
                  </span>
                </Button>
              );
            })}
          </Col>
        </Row>
      </div>
      <Row className="mt-1">
        <Col>
          {dailySummary.length ? (
            <DailySummaryAccordion
              data={dailySummary} // daily summary list
              selectedDate={selectedDate} // which accordion is open
              onDateSelect={(date: string) => {
                setSelectedDate(date);
                setPageNumber(1); // reset pagination
                setAllLoaded(false);
              }}
              selectedYear={selectedYear}
              activityData={activityData} // data to show inside accordion
              onLoadMore={() => {
                setPageNumber((prev) => prev + 1);
              }} // show more
              onShowLess={() => {
                setPageNumber(1);
                setAllLoaded(false);
              }}
              allLoaded={allLoaded} // toggle show more/less
              totalRows={totalRows}
            />
          ) : (
            <>
              <div className="nodataactivityass">No Record Found</div>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default CaseActivityList;
